/**
 * useGameShell — hook principal pour câbler une session jeu à React.
 *
 * Deux modes d'usage :
 *
 *   1. **Standalone** (sans provider). Crée son propre controller :
 *      ```ts
 *      const shell = useGameShell({ config, adapter, buildStimulus, grader });
 *      ```
 *      Cycle de vie attaché au composant : `controller.start()` au mount,
 *      `controller.stop()` à l'unmount.
 *
 *   2. **Via context** (à l'intérieur de `<GameShell>`). Lit le controller créé
 *      en amont par le composant `<GameShell>` :
 *      ```ts
 *      const shell = useGameShellFromContext();
 *      ```
 *
 * Retourne un objet :
 *   - `controller` : référence au `GameRunController` (start, stop, send, etc.)
 *   - `snapshot`   : snapshot synchronisé via `useSyncExternalStore`
 *   - `phase`, `isPaused`, `isAborted` : raccourcis dérivés
 *   - `startSession`, `pause`, `resume`, `abort`, `skipDemo`, `markDemoDone`,
 *     `markWarmupDone`, `markWrapUpDone`, `complete` : helpers d'event sending
 */
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useSyncExternalStore } from 'react';
import { createGameRun, } from '../runtime';
import { GameShellContext } from './context';
/**
 * Standalone : crée un controller et l'attache au cycle de vie du composant.
 * Note : les changements d'options ne réinstancient PAS le controller (volontaire :
 * une session jeu est immutable une fois lancée).
 */
export function useGameShell(opts) {
    const ctrlRef = useRef(null);
    if (ctrlRef.current === null) {
        ctrlRef.current = createGameRun(opts);
    }
    const ctrl = ctrlRef.current;
    // useSyncExternalStore — adapter signature controller.subscribe → React.
    const subscribe = useMemo(() => (notify) => ctrl.subscribe(() => notify()), [ctrl]);
    const getSnapshot = useMemo(() => () => ctrl.getSnapshot(), [ctrl]);
    const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    useEffect(() => {
        ctrl.start();
        return () => {
            ctrl.stop();
        };
    }, [ctrl]);
    return useMemo(() => ({
        controller: ctrl,
        snapshot,
        phase: snapshot.phase,
        isPaused: snapshot.isPaused,
        isAborted: snapshot.isAborted,
        startSession: (sessionId) => ctrl.startSession(sessionId ?? null),
        pause: (reason) => ctrl.pause(reason),
        resume: () => ctrl.resume(),
        abort: (reason) => ctrl.abort(reason),
        skipDemo: () => ctrl.skipDemo(),
        markDemoDone: () => ctrl.markDemoDone(),
        markWarmupDone: () => ctrl.markWarmupDone(),
        markWrapUpDone: () => ctrl.markWrapUpDone(),
        complete: () => ctrl.complete(),
    }), [ctrl, snapshot]);
}
/**
 * Lit le controller depuis le `<GameShell>` ancêtre. Lance si absent.
 */
export function useGameShellFromContext() {
    const ctrl = useContext(GameShellContext);
    if (!ctrl) {
        throw new Error('[game-shell] useGameShellFromContext must be used inside <GameShell>');
    }
    const subscribe = useMemo(() => (notify) => ctrl.subscribe(() => notify()), [ctrl]);
    const getSnapshot = useMemo(() => () => ctrl.getSnapshot(), [ctrl]);
    const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    return useMemo(() => ({
        controller: ctrl,
        snapshot,
        phase: snapshot.phase,
        isPaused: snapshot.isPaused,
        isAborted: snapshot.isAborted,
        startSession: (sessionId) => ctrl.startSession(sessionId ?? null),
        pause: (reason) => ctrl.pause(reason),
        resume: () => ctrl.resume(),
        abort: (reason) => ctrl.abort(reason),
        skipDemo: () => ctrl.skipDemo(),
        markDemoDone: () => ctrl.markDemoDone(),
        markWarmupDone: () => ctrl.markWarmupDone(),
        markWrapUpDone: () => ctrl.markWrapUpDone(),
        complete: () => ctrl.complete(),
    }), [ctrl, snapshot]);
}
//# sourceMappingURL=useGameShell.js.map